<template>
    <div class="pretorInput">
        <label v-if="label != ''" class="pretorField__label">
            <label v-if="required" class="required" title="OBLIGATORIO">*</label>
            {{ label }}
        </label>

        <div v-if="icon != ''" class="input-with-icon pretorFormWrapper" :style="style" :class="classObject">
            <span class="input-icon">
                <i :class="icon"></i>
            </span>

            <input ref="input"
                v-model="value"
                class="pretorForm"
                :class="classObject"
                :placeholder="placeholder"
                :disabled="disabled"
                :autocomplete="autocomplete"
                :style="style"
                :type="isPassword ? 'password' : type"
                :oninput="onInputHandler"
                @click="focused=true"
                @keydown="KeydownHandler"
                @focus="onFocusHandler"
                @blur="onBlurHandler">
        </div>

        <div v-else :style="style" class="pretorFormWrapper">
            <input ref="input"
                v-model="value"
                class="pretorForm"
                :placeholder="placeholder"
                :disabled="disabled"
                :autocomplete="autocomplete"
                :class="classObject"
                :style="style"
                :type="isPassword ? 'password' : type"
                :name="isPassword ? 'notsearch_password' : ''"
                :oninput="onInputHandler"
                @click="focused = true"
                @keydown="KeydownHandler"
                @focus="onFocusHandler"
                @blur="onBlurHandler">
        </div>

        <PretorFieldError :message="activeError" :hint="hint"/>
    </div>
</template>

<script>
import PretorFieldError from '@/components/Generic/FieldError.vue';

export default{
    name: 'PretorInput',
    components: {
        PretorFieldError
    },
    props:{
        type: {
            type: String,
            required: false,
            default: 'text'
        },
        label:{
            type: String,
            required: false,
            default: ''
        },
        placeholder:{
            type: String,
            required: false,
            default: ''
        },
        style:{
            type: String,
            required: false,
            default: ''
        },
        required:{
            type: Boolean,
            required: false,
            default: false
        },
        validate: {
            type: RegExp,
            required: false,
            default: null
        },
        icon: {
            type: String,
            required: false,
            default: ''
        },
        onInput: {
            type: Function,
            required: false,
            default: null
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false
        },
        validateMessage: {
            type: String,
            required: false,
            default: ''
        },
        isPassword: {
            type: Boolean,
            required: false,
            default: false
        },
        // Chrome ignores autocomplete="off" for fields it detects as an address/
        // contact field (e.g. email) and still shows its "Saved info" suggestions.
        // An unrecognized token (mirrors the "one-time-code" trick already used
        // in LoginView) reliably suppresses it; override per-field when needed.
        autocomplete: {
            type: String,
            required: false,
            default: 'off'
        },
        OnEnter: {
            type: Function,
            required: false,
            default: () => {}
        },
        AutoCaps: {
            type: Boolean,
            required: false,
            default: true
        },
        Class: {
            type: String,
            required: false,
            default: ''
        },
        maxLength: {
            type: Number,
            required: false,
        },
        useDebounce: {
            type: Boolean,
            required: false,
            default: false
        },
        debounceTimeInMilisenconds: {
            type: Number,
            required: false,
            default: 200
        },
        // Persistent helper text shown under the field (e.g. password rules).
        // Hidden while an error is showing so the two never stack.
        hint: {
            type: String,
            required: false,
            default: ''
        },
        // Externally-controlled error (e.g. "passwords don't match").
        // When non-empty it always wins and forces the invalid state.
        errorMessage: {
            type: String,
            required: false,
            default: ''
        },
        // Message shown inline when a required field is left empty on validation.
        requiredMessage: {
            type: String,
            required: false,
            default: 'Este campo es obligatorio.'
        },
        // Focus / blur hooks so consumers can react (e.g. reveal a checklist,
        // run cross-field validation). Kept generic and optional.
        OnFocus: {
            type: Function,
            required: false,
            default: () => {}
        },
        OnBlur: {
            type: Function,
            required: false,
            default: () => {}
        },
    },
    data(){
        return {
            value: '',
            valid: false,
            showPassword: false,
            alreadyValidated: false
        }
    },
    computed: {
        // Single source of truth for what error (if any) to surface, in priority.
        activeError() {
            if (this.errorMessage) {
                return this.errorMessage;
            }
            if (this.maxLength && this.value.length > this.maxLength) {
                return `Máximo ${this.maxLength} caracteres.`;
            }
            if (!this.alreadyValidated) {
                return '';
            }
            if (this.required && this.value.trim().length === 0) {
                return this.requiredMessage;
            }
            if (!this.valid && this.value.length > 0 && this.validateMessage) {
                return this.validateMessage;
            }
            return '';
        },
        classObject() {
            const classes = {
                'invalid': this.activeError !== ''
            };
            if (this.Class !== '') {
                classes[this.Class] = true;
            }
            return classes;
        }
    },

    mounted(){
        this.valid = !this.required;

        if (this.useDebounce && this.onInput) {
            this.debouncedInput = this.debounce((val) => {
                this.onInput(val);
            }, this.debounceTimeInMilisenconds);
        }
    },

    unmounted(){
        if(this.debounceTimeoutId){
            clearTimeout(this.debounceTimeoutId);
            this.debounceTimeoutId = null;
        }
    },

    watch: {
        required: function(newValue, oldValue) {
        if (newValue !== oldValue) {
            this.CheckIsValid();
        }
        }
    },
    methods: {
        SetFocus(){
            this.$refs.input.focus();
        },

        KeydownHandler(event){
            if(event.key == 'Enter'){
                this.OnEnter();
            }
        },

        onFocusHandler(){
            this.OnFocus();
        },

        // On blur, surface local validation for a field the user actually filled.
        // Empty fields stay quiet here (they reset on clear) — required-empty is
        // still enforced on submit via GetValid().
        onBlurHandler(){
            this.CheckIsValid();

            if(this.value.trim().length > 0 && !this.valid){
                this.alreadyValidated = true;
            }

            this.OnBlur();
        },

        GetValue(){
            return this.value;
        },

        SetValue(value){
            this.value = value;
            this.onInputHandler();
            this.CheckIsValid();
        },

        CheckIsValid(){
            let value = this.value.trim();
            let valid = true;

            if(this.validate && value.length > 0){
                valid = this.validate.test(value);
            }

            if(this.required){
                valid = value.length > 0 && valid;
            }

            if(this.maxLength){
                valid = value.length <= this.maxLength && valid;
            }

            this.valid = valid;
        },

        ClearField(){
            this.value = '';
            this.valid = !this.required;
            this.alreadyValidated = false;
        },

        ClearValid(){
            this.alreadyValidated = false;
        },

        onInputHandler(){
            if(this.value.length === 0 && this.alreadyValidated){
                this.alreadyValidated = false;
            }

            if(this.AutoCaps){
                let textarea = this.$refs.input;
                let start = textarea.selectionStart;
                let end = textarea.selectionEnd;

                this.value = this.value.toUpperCase();

                this.$nextTick(() => {
                    textarea.setSelectionRange(start, end);
                });
            }

            if (this.onInput) {
                if (this.useDebounce && this.debouncedInput) {
                    this.debouncedInput(this.value);
                }
                else {
                    this.onInput(this.value);
                }
            }

            this.CheckIsValid();
        },

        // Función genérica de debounce
        debounce(fn, delay) {
            return (...args) => {
                clearTimeout(this.debounceTimeoutId);
                this.debounceTimeoutId = setTimeout(() => fn(...args), delay);
            };
        },

        GetValid(){
            if(!this.valid){
                this.alreadyValidated = true;
            }

            return this.valid;
        }
    }
}
</script>

<style scoped>
input::-ms-reveal,
input::-ms-clear {
    display: none;
}

/* Overrides the global .input-icon centering (top:50% + translateY(-50%)),
   which mis-centers icon-font glyphs like bi-search because bootstrap-icons
   applies vertical-align:-.125em to its ::before content. Spanning the full
   wrapper height and flex-centering the glyph inside it sidesteps that
   font-metric quirk entirely. */
.input-icon {
    top: 0;
    bottom: 0;
    transform: none;
    display: flex;
    align-items: center;
}

/* Generic focus + validity feedback for every consumer of this input.
   `.pretorForm` ships with outline:none — restore a focus indicator here. */
.pretorForm {
    transition: border-color 160ms ease, background-color 160ms ease;
}

.pretorForm:focus {
    /* Neutral focus: a soft gray border, no ring — visible without borrowing
       the brand-purple accent. Persists while the field is active (focused). */
    border-color: #a7a3b0;
}

.pretorForm.invalid:focus {
    border-color: #d98b84;
}

/* Own wrapper (mirrors Multiselect.vue's `.multiselect` root) so this
   component always stacks label-over-field regardless of the parent's
   flex-direction — without it, the label/input/error nodes render as
   separate fragment roots and inherit the parent flex axis directly. */
.pretorInput {
    display: flex;
    flex-direction: column;
}

.pretorField__label {
    font-size: 12px;
    color: gray;
}
</style>
